// @flow
import * as ActionsType from './constants';
import * as OrganizationActionType from '../organization/constants';
import * as UserActionType from '../user/constants';

import { getLoggedInUser } from '../../helpers/authUtils';
import { clearPersistedSubscriptionEndMs } from '../../helpers/subscriptionLockout';
import { setSession } from './saga';

const INIT_STATE = {
  user: getLoggedInUser(),
  loading: false,
  invalidTokenStatus: null,
  error: null,
  systemAdminAllowed: null,
};

type AuthAction = { type: string, payload: {} | string };
type State = { user?: {} | null, loading?: boolean, +value?: boolean };

const Auth = (state: State = INIT_STATE, action: AuthAction) => {
  switch (action.type) {
    case ActionsType.LOGIN_USER:
      return { ...state, loading: true };
    case ActionsType.LOGIN_USER_SUCCESS:
      return { ...state, user: action.payload, loading: false, error: null };
    case ActionsType.LOGIN_USER_FAILED:
      return { ...state, error: action.payload, loading: false };

    case ActionsType.REGISTER_USER:
      return { ...state, loading: true };
    case ActionsType.REGISTER_USER_SUCCESS:
      return { ...state, user: action.payload, loading: false, error: null };
    case ActionsType.REGISTER_USER_FAILED:
      return { ...state, error: action.payload, loading: false };

    case ActionsType.REGISTER_USER_EMAIL:
      return { ...state, loading: true, error: null };
    case ActionsType.REGISTER_USER_EMAIL_SUCCESS:
      return { ...state, loading: false };
    case ActionsType.REGISTER_USER_EMAIL_FAILED:
      return { ...state, error: action.payload, loading: false };

    case ActionsType.VALIDATE_REGISTRATION_EMAIL:
      return { ...state, loading: true, error: null, invalidTokenStatus: null };
    case ActionsType.VALIDATE_REGISTRATION_EMAIL_SUCCESS:
      return { ...state, loading: false };
    case ActionsType.VALIDATE_REGISTRATION_EMAIL_FAILED:
      return {
        ...state,
        invalidTokenStatus: action.payload.status,
        error: action.payload.error,
        loading: false,
      };

    case ActionsType.CLEAR_LOGIN_REDIRECT: {
      // Persist the cleared session too, so a reload does not bring the redirect back.
      const current = state.user ?? getLoggedInUser();
      if (!current?.redirectUrl) return state;
      const { redirectUrl, ...user } = current;
      setSession(user);
      return { ...state, user };
    }

    case ActionsType.LOGOUT_USER:
      return { ...state, user: null, systemAdminAllowed: null };

    case ActionsType.SET_SYSTEM_ADMIN_ACCESS:
      return { ...state, systemAdminAllowed: action.payload };

    case ActionsType.FORGET_PASSWORD:
      return { ...state, loading: true };
    case ActionsType.FORGET_PASSWORD_SUCCESS:
      return { ...state, passwordResetStatus: action.payload, loading: false, error: null };
    case ActionsType.FORGET_PASSWORD_FAILED:
      return { ...state, error: action.payload, loading: false };

    case UserActionType.UPGRADE_OR_DOWNGRADE_SUBSCRIPTION_SUCCESS: {
      clearPersistedSubscriptionEndMs();
      const newUser = { ...state.user, currentPlan: action.payload.newPlanId };
      setSession(newUser);
      return { ...state, user: newUser };
    }
    case UserActionType.CANCEL_SUBSCRIPTION_SUCCESS: {
      clearPersistedSubscriptionEndMs();
      const newUser = {
        ...state.user,
        currentPlan: action.payload.newPlanId,
        subscriptionPeriodEndUtc: null,
      };
      setSession(newUser);
      return { ...state, user: newUser };
    }
    case UserActionType.UPDATE_SUCCESSFUL_PLAN_SUCCESS: {
      clearPersistedSubscriptionEndMs();
      const newUser = {
        ...state.user,
        currentPlan: action.payload.newPlanId,
        subscriptionPeriodEndUtc: null,
        currentPlanEndDate: null,
      };
      setSession(newUser);
      return { ...state, user: newUser };
    }

    case OrganizationActionType.ACCEPT_INVITATION_SUCCESS: {
      return {
        ...state,
        user: {
          ...state.user,
          organizationId: action.payload.organizationId,
        },
      };
    }
    case OrganizationActionType.CREATE_ORGANIZATION_SUCCESS:
      console.log('CREATE_ORGANIZATION_SUCCESS action received:', action.payload);
      const newUser = {
        ...state.user,
        token: action.payload.newUserToken,
        organizationId: action.payload.organizationId || 1, // Set a temporary organizationId if not provided
      };
      console.log('New user object:', newUser);
      setSession(newUser);
      return { ...state, user: newUser };

    case OrganizationActionType.REACTIVATE_ORGANIZATION_SUCCESS:
      console.log('REACTIVATE_ORGANIZATION_SUCCESS action received:', action.payload);
      const reactivatedUser = {
        ...state.user,
        organizationId: action.payload.organizationId,
        isManager: true, // Owner is restored as manager
      };
      console.log('Reactivated user object:', reactivatedUser);
      setSession(reactivatedUser);
      return { ...state, user: reactivatedUser };

    case OrganizationActionType.REMOVE_USER_FROM_ORGANIZATION_SUCCESS: {
      // If the removed user is the current logged-in user, clear their organizationId
      const removedUserId = action.payload?.userId;
      const currentUserId = state.user?.id || state.user?.Id;
      if (removedUserId && currentUserId && removedUserId === currentUserId) {
        const updatedUser = {
          ...state.user,
          organizationId: null,
          isManager: false,
        };
        setSession(updatedUser);
        return { ...state, user: updatedUser };
      }
      return { ...state };
    }

    case OrganizationActionType.LOAD_ORGANIZATION_DETAILS_FAILED: {
      // If organization details fail to load, it means the user doesn't have an organization
      // Clear organizationId from user state to prevent showing organization tabs
      if (state.user?.organizationId) {
        const updatedUser = {
          ...state.user,
          organizationId: null,
          isManager: false,
        };
        setSession(updatedUser);
        return { ...state, user: updatedUser };
      }
      return { ...state };
    }

    default:
      return { ...state };
  }
};

export default Auth;
